/* eslint-disable @thebeansgroup/sb/components-file-structure */
import PropTypes from 'prop-types'
import React from 'react'

import { withI18n } from '@thebeansgroup/i18n'

import { ROUTES } from '@src/authorisation/children/router/constants'
import {
  responseHasNetworkError,
  trackFormErrorEvent,
  getNetworkErrorText,
  asyncRequestWithFetch
} from '@src/authorisation/helpers'
import CloudflareTurnstile from '@src/components/cloudflare_turnstile'

import Form from '@components/form'

import {
  EmailField,
  PasswordField,
  SubmitButton
} from './children/form_components'
import { LAST_ATTEMPT_FALLBACK, LOCKED_ACCOUNT_FALLBACK } from './constants'
import {
  getEmailError,
  getPasswordError,
  isFormValid,
  responseHasEmailNotFoundError,
  responseHasIncorrectPasswordError,
  canDisplayWrongPasswordScreen,
  responseHasLastAttemptError,
  responseHasLockedAccountError,
  repsonseHasRecaptchaError
} from './helpers'

export class LogInForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      emailError: '',
      emailErrorAction: null,
      emailErrorActionText: '',
      password: '',
      passwordError: '',
      loading: false,
      recaptchaError: false,
      recaptchaToken: ''
    }
    this.bindHandlers()
  }

  bindHandlers() {
    this.onEmailValueChange = this.onEmailValueChange.bind(this)
    this.onEmailFocus = this.onEmailFocus.bind(this)
    this.onEmailBlur = this.onEmailBlur.bind(this)
    this.onPasswordValueChange = this.onPasswordValueChange.bind(this)
    this.onPasswordFocus = this.onPasswordFocus.bind(this)
    this.onPasswordBlur = this.onPasswordBlur.bind(this)
    this.onLogInClick = this.onLogInClick.bind(this)
    this.handleLogInSuccess = this.handleLogInSuccess.bind(this)
    this.handleLogInFailure = this.handleLogInFailure.bind(this)
    this.handleRecaptchaToken = this.handleRecaptchaToken.bind(this)
    this.handleRecaptchaError = this.handleRecaptchaError.bind(this)
    this.handleRecaptchaReset = this.handleRecaptchaReset.bind(this)
    this.submitLogInForm = this.submitLogInForm.bind(this)
  }

  onEmailValueChange(field, value) {
    this.setState({ emailError: '' })
    this.props.emailValueChangeHandler(value)
  }

  onEmailFocus() {
    this.setState({ emailError: '' })
  }

  onEmailBlur() {
    const { t, email } = this.props
    const error = getEmailError(t, email)
    this.setState({
      emailError: error,
      emailErrorAction: null,
      emailErrorActionText: ''
    })
    trackFormErrorEvent('email', error, email)
  }

  onPasswordValueChange(field, value) {
    this.setState({
      passwordError: '',
      password: value
    })
  }

  onPasswordFocus() {
    this.setState({ passwordError: '' })
  }

  onPasswordBlur() {
    const { t } = this.props
    const { password } = this.state
    const error = getPasswordError(t, password)
    this.setState({
      passwordError: error
    })
    trackFormErrorEvent('password', error, password)
  }

  runValidations(callback) {
    const { t, email } = this.props
    const { password } = this.state
    const emailError = getEmailError(t, email)
    const passwordError = getPasswordError(t, password)
    this.setState(
      {
        emailError,
        passwordError
      },
      callback
    )
    trackFormErrorEvent('email', emailError, email)
    trackFormErrorEvent('password', passwordError, password)
  }

  handleEmailNotFound() {
    const { t, signUpClickHandler, email } = this.props
    const emailError = t('e_account_not_found')
    this.setState({
      loading: false,
      emailError,
      emailErrorAction: signUpClickHandler,
      emailErrorActionText: `${t('a_sign_up')}.`,
      recaptchaError: true
    })

    trackFormErrorEvent('email', emailError, email)
  }

  handleNetworkError() {
    const { t } = this.props
    const emailError = getNetworkErrorText(t)

    this.setState({
      loading: false,
      emailError
    })

    trackFormErrorEvent('email', emailError)
  }

  handleLastAttempt() {
    const passwordError = LAST_ATTEMPT_FALLBACK

    this.setState({
      loading: false,
      passwordError,
      recaptchaError: true
    })
  }

  handleLockedAccount() {
    const passwordError = LOCKED_ACCOUNT_FALLBACK

    this.setState({
      loading: false,
      passwordError,
      recaptchaError: true
    })
  }

  handleIncorrectPassword(data) {
    if (canDisplayWrongPasswordScreen(data)) {
      this.props.wrongPasswordHandler(data)
    } else {
      const { t } = this.props
      const { password } = this.state
      const passwordError = t('e_password_incorrect')
      this.setState({
        loading: false,
        passwordError,
        recaptchaError: true
      })
      trackFormErrorEvent('password', passwordError, password)
    }
  }

  handleFormError(data) {
    this.setState({ loading: false })
    this.props.requestErrorHandler(data)
  }

  handleRecaptchaToken(recaptchaToken) {
    this.setState({ recaptchaToken })
  }

  handleRecaptchaError() {
    this.setState({
      loading: false
    })
  }

  handleRecaptchaReset() {
    this.setState({ recaptchaError: false })
  }

  handleLogInFailure(data) {
    if (repsonseHasRecaptchaError(data)) {
      return this.handleRecaptchaError()
    }
    if (responseHasEmailNotFoundError(data)) {
      this.handleEmailNotFound()
    } else if (responseHasIncorrectPasswordError(data)) {
      this.handleIncorrectPassword(data)
    } else if (responseHasLastAttemptError(data)) {
      this.handleLastAttempt()
    } else if (responseHasLockedAccountError(data)) {
      this.handleLockedAccount()
    } else if (responseHasNetworkError(data)) {
      this.handleNetworkError()
    } else {
      this.handleFormError(data)
    }
  }

  handleLogInSuccess(data) {
    this.props.logInHandler(data)
  }

  submitLogInForm() {
    this.setState({ loading: true })
    const { email, token } = this.props
    const { password, recaptchaToken } = this.state

    asyncRequestWithFetch(
      token,
      ROUTES.SERVER.LOG_IN,
      {
        user: { email, password },
        'cf-turnstile-response': recaptchaToken
      },
      this.handleLogInSuccess,
      this.handleLogInFailure
    )
  }

  onLogInClick() {
    this.runValidations(() => {
      if (isFormValid(this.state)) {
        return this.submitLogInForm()
      }
    })
  }

  renderEmail() {
    return (
      <EmailField
        t={this.props.t}
        email={this.props.email}
        emailError={this.state.emailError}
        emailErrorAction={this.state.emailErrorAction}
        emailErrorActionText={this.state.emailErrorActionText}
        emailValueHandler={this.onEmailValueChange}
        emailBlurHandler={this.onEmailBlur}
        emailFocusHandler={this.onEmailFocus}
        consumerGroup={this.props.consumerGroup}
      />
    )
  }

  renderPassword() {
    return (
      <PasswordField
        t={this.props.t}
        password={this.state.password}
        passwordError={this.state.passwordError}
        passwordValueHandler={this.onPasswordValueChange}
        passwordBlurHandler={this.onPasswordBlur}
        passwordFocusHandler={this.onPasswordFocus}
      />
    )
  }

  renderSubmitButton() {
    return (
      <SubmitButton
        t={this.props.t}
        loading={this.state.loading}
        formSubmitHandler={this.onLogInClick}
        valid={isFormValid(this.state)}
      />
    )
  }

  renderTurnstile() {
    return (
      <CloudflareTurnstile
        onSuccess={this.handleRecaptchaToken}
        onError={this.handleRecaptchaError}
        error={this.state.recaptchaError}
        resetError={this.handleRecaptchaReset}
        action='login'
      />
    )
  }

  render() {
    return (
      <Form>
        {this.renderEmail()}
        {this.renderPassword()}
        {this.renderTurnstile()}
        {this.renderSubmitButton()}
      </Form>
    )
  }
}

LogInForm.propTypes = {
  t: PropTypes.func.isRequired,
  logInHandler: PropTypes.func.isRequired,
  signUpClickHandler: PropTypes.func.isRequired,
  requestErrorHandler: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  emailValueChangeHandler: PropTypes.func.isRequired,
  wrongPasswordHandler: PropTypes.func.isRequired,
  consumerGroup: PropTypes.string.isRequired
}

export default withI18n(LogInForm)
